
import React, { useEffect, useRef, useState } from "react";
import{ useSelector} from "react-redux";
import {
    X,
    Send,
    LucideHardHat,
    MessageCircleMore,
    LoaderCircle,
} from "lucide-react";
import { useAttendanceSearchAllUsersMutation } from "../../features/attendanceApiSlice";
import ChatMessageItem from "./ChatMessageItem";

interface AttendanceChatDrawerProps {
    open: boolean;
    onClose: () => void;
}

interface ChatItem {
    id: string;
    role: "user" | "assistant";
    text: string;
    messageContent?: string;
    error?: string;
    sources?: any[];
}

export default function AttendanceChatWindow({
                                                 open,
                                                 onClose,
                                             }: AttendanceChatDrawerProps) {
    const [sessionId] = useState(() => crypto.randomUUID());
    const [input, setInput] = useState("");
    const [messages, setMessages] = useState<ChatItem[]>([]);
    const [sendMessage, { isLoading }] =
        useAttendanceSearchAllUsersMutation();

    const {userInfo} = useSelector((state: any) => state).auth;
    const userId = userInfo?.id;

    const bottomRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages, open]);

    useEffect(() => {
        if (!open) return;

        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === "Escape") onClose();
        };

        window.addEventListener("keydown", handleEscape);
        return () => window.removeEventListener("keydown", handleEscape);
    }, [open, onClose]);

    const handleSend = async (
        event: React.FormEvent<HTMLFormElement>
    ) => {
        event.preventDefault();

        const question = input.trim();

        if (!question || isLoading) return;

        const userMessage: ChatItem = {
            id: crypto.randomUUID(),
            role: "user",
            text: question,
            messageContent: question,
        };

        setMessages((previous) => [...previous, userMessage]);
        setInput("");

        try {
            const response = await sendMessage({
                sessionId,
                messageContent: question,
                userId: userId,
                role: "User",
            }).unwrap();

            const answer = response.messageContent ?? response.text ?? "";

            setMessages((previous) => [
                ...previous,
                {
                    id: response.id ?? crypto.randomUUID(),
                    role: "assistant",
                    text: answer,
                    messageContent: answer,
                    sources: response.sources ?? [],
                },
            ]);
        } catch (error: any) {
            const errorMessage =
                error?.data?.message ??
                error?.data?.title ??
                error?.error ??
                "Unable to get an attendance response. Please try again.";

            setMessages((previous) => [
                ...previous,
                {
                    id: crypto.randomUUID(),
                    role: "assistant",
                    text: "",
                    error: errorMessage,
                },
            ]);
        }
    };

    if (!open) return null;

    return (
        <>
            {/* Backdrop */}
            <button
                type="button"
                aria-label="Close attendance assistant"
                onClick={onClose}
                className="fixed inset-0 z-40 cursor-default bg-black/50"
            />

            {/* Slide-in panel */}
            <aside
                role="dialog"
                aria-modal="true"
                aria-label="Attendance AI assistant"
                className="
                    fixed right-0 top-0 z-50 flex h-dvh
                    w-full max-w-xl flex-col
                    border-l border-gray-200 bg-white shadow-2xl
                    dark:border-gray-800 bg-gray-400
                    animate-in slide-in-from-right duration-200
                "
            >
                {/* Header */}
                <header className="flex items-center justify-between border-b border-gray-200 p-4 dark:border-gray-800">
                    <div className="flex items-center gap-3">
                        <div className="rounded-xl bg-blue-100 p-2 text-blue-700 dark:bg-blue-500/10 dark:text-blue-400">
                            <LucideHardHat size={24} color={"black"} />
                        </div>
                        <div>
                            <h2 className="font-medium text-lg text-gray-900 dark:text-black">
                                Employee Assistant
                            </h2>

                        </div>
                    </div>

                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close chat"
                        className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800"
                    >
                        <X size={20} />
                    </button>
                </header>

                {/* Messages */}
                <div className="flex-1 space-y-3 overflow-y-auto p-4">
                    {messages.length === 0 && (
                        <div className="mx-auto mt-10 max-w-sm text-center">
                            <MessageCircleMore
                                size={36}
                                className="mx-auto mb-3 text-blue-500"
                            />
                            <h3 className="font-semibold text-gray-900 dark:text-white">
                                How can I help?
                            </h3>
                            <p className="mt-2 text-sm font-semibold text-gray-900">
                                Ask about recorded work hours, missing
                                sign-outs, or attendance by project.
                            </p>

                            <div className="mt-5 flex flex-wrap justify-center gap-2">
                                {[
                                    "How many hours did I work this week?",
                                    "Are there any missing sign-outs?",
                                    "Summarize today's attendance",
                                ].map((suggestion) => (
                                    <button
                                        key={suggestion}
                                        type="button"
                                        onClick={() => setInput(suggestion)}
                                        className="rounded-full border border-gray-300 px-3 py-2 text-left text-sm text-gray-900 hover:bg-gray-100 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
                                    >
                                        {suggestion}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {messages.map((message) => (
                        <ChatMessageItem
                            key={message.id}
                            message={message}
                            inProgress={false}
                            showSources={true}
                        />
                    ))}

                    {isLoading && (
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                            <LoaderCircle
                                size={18}
                                className="animate-spin"
                            />
                            Analyzing attendance records...
                        </div>
                    )}

                    <div ref={bottomRef} />
                </div>

                {/* Composer */}
                <form
                    onSubmit={handleSend}
                    className="border-t border-gray-200 p-4 dark:border-gray-800"
                >
                    <div className="flex items-end gap-2 rounded-2xl border border-gray-300 bg-gray-50 p-2 focus-within:border-blue-500 dark:border-gray-700 dark:bg-[#171717]">
                        <textarea
                            value={input}
                            onChange={(event) =>
                                setInput(event.target.value)
                            }
                            onKeyDown={(event) => {
                                if (
                                    event.key === "Enter" &&
                                    !event.shiftKey
                                ) {
                                    event.preventDefault();
                                    event.currentTarget.form?.requestSubmit();
                                }
                            }}
                            rows={1}
                            maxLength={4000}
                            placeholder="Ask an attendance question..."
                            aria-label="Attendance question"
                            className="max-h-32 min-h-10 flex-1 resize-y bg-transparent p-2 text-sm text-gray-900 outline-none dark:text-white"
                        />

                        <button
                            type="submit"
                            disabled={!input.trim() || isLoading}
                            aria-label="Send message"
                            className="rounded-xl bg-blue-600 p-3 text-white transition hover:bg-blue-500 disabled:cursor-not-allowed disabled:opacity-40"
                        >
                            <Send size={18} />
                        </button>
                    </div>

                </form>
            </aside>
        </>
    );
}
